// src/app/argent/useChargementDonnees.js — le chargement de session :
// configuration, données de facturation (et leur jeton), journal, incidents,
// compta, stock ; les lectures ratées (F13).
// CHEMIN ARGENT. Déplacé octet pour octet depuis src/App.jsx (P-3.5, squelette,
// partie 2) : seuls changent l'emballage (signature, retour) et les imports.

import { storage, supabase } from '../../supabase'
import { useEffect } from 'react'
import { mapV2IncidentsToKv } from '../../lib/incidents-helpers'
import { listIncidents } from '../../lib/incidents-api'
import { getAllLogsFlat } from '../../lib/sousloc-helpers'
import { migrateData } from '../../lib/data-migration'
import { stockApi } from '../../lib/stock-api'
import { comptaApi } from '../../lib/compta-api'
import { fetchCfg } from '../../lib/cfg-api'
import { estIllisible } from '../../lib/reponse-json'
// Même mécanique, pour le blob de facturation (jeton de version, 2026-09-08).
import { jetonDetenu, fetchData, MESSAGE_DATA_LECTURE_IMPOSSIBLE, pannesDuChargement, adopterJeton } from '../../lib/data-api'
import { CHARGES_FIXES_DEFAULT } from '../../lib/domain-constants'

export function useChargementDonnees({
  setCfg, setLecturesIllisibles, setData, setFactLog, setIncidents, setCompta, setStockCentral,
  setDepots, setAchats, currentUser,
}) {
  // Chargement des données, déclenché par le changement d'utilisateur — hors du verrou auth.
  useEffect(() => {
    if (!currentUser) return
    // Permissions 42 Phase 2 — le rôle 'proprietaire' a son ESPACE PROPRIO dédié
    // (ProprioApp) qui lit EXCLUSIVEMENT les endpoints proprio_* cloisonnés. Il
    // n'a aucun usage des blobs admin (cfg/data/compta/stock) — et ces fetchs
    // sont requireAdmin → 403. Skip pour ne pas générer d'appels inutiles/403
    // (défense-en-profondeur, miroir du skip menage).
    if (currentUser.role === 'proprietaire') return
    let cancelled = false
    // F13 (tour 3) : le jeton détenu AU DÉBUT de ce chargement. Seul un jeton
    // adopté PENDANT lui (relecture réussie) rend sa panne périmée — pas celui
    // d'une session ou d'un chargement d'avant.
    const jetonDebut = jetonDetenu()
    // Collecte des refus « ce n'est pas une donnée » du chargement de session.
    // Un tableau local et non un état : les six lectures partent en parallèle,
    // six `setState` concurrents se marcheraient dessus. On pose l'état une
    // seule fois, quand tout est retombé.
    const illisibles = []
    // `siPanne` (F13) : le message à montrer pour une panne qui n'est pas
    // « illisible » (réseau coupé, 500). Sans lui, comportement inchangé.
    const replier = (etiquette, valeur, siPanne) => (e) => {
      console.error(`[${etiquette}] fetch failed`, e)
      if (estIllisible(e)) illisibles.push(e.message)
      else if (siPanne) illisibles.push(siPanne)
      return valeur
    }
    ;(async () => {
      try {
        // Lot 1 : STOCK lu via /api/stock (tables V2). Lot 2 : COMPTA lu via
        // /api/compta (table compta_config) — admin-only, donc fetch gardé au
        // rôle admin (le ménage n'a aucun usage de la compta). Les deux gardent
        // un miroir KV dual-write (rollback soak). cfg/data/factlog/incidents
        // restent sur KV (hors scope).
        const isAdminUser = currentUser?.role === 'admin'
        const [cfgRes,dr,fr,incRows,compta,stock] = await Promise.all([
          // Lot B : ADMIN charge cfg via /api/cfg (table cfg_config). Le MÉNAGE
          // ne lit plus la configuration : ses écrans passent par les portes
          // du serveur (journée, guichet, catalogue sans prix, plus bas).
          isAdminUser
            ? fetchCfg().catch(replier('cfg', null))
            : currentUser.role === 'menage'
              ? Promise.resolve(null)
              : storage.get('fbz8-cfg').then(r => r ? JSON.parse(r.value) : null).catch(() => null),
          // ⚠️ PASSE PAR /api/data, PAS par storage : c'est la LECTURE qui
          // fournit le jeton de version (src/lib/data-api.js), adopté plus bas
          // avec la donnée. Une lecture
          // directe laisserait le jeton vide et TOUT enregistrement serait
          // ensuite refusé en 409 « jeton absent » — la garde deviendrait un
          // blocage. Le repli null garde le comportement d'avant en cas
          // d'échec (la garde d'abort plus bas s'en charge).
          // F13 : pour l'admin, une panne qui n'est pas « illisible » (réseau
          // coupé, 500) n'en est pas moins une lecture ratée — le bandeau le
          // dit, et l'écran Argent affiche « — » au lieu de zéros. Les autres
          // rôles n'ont pas accès à ces données (`requireAdmin`, 403) : on ne
          // les demande pas, comme pour la compta juste en dessous.
          isAdminUser
            ? fetchData().catch(replier('data', null, MESSAGE_DATA_LECTURE_IMPOSSIBLE))
            : Promise.resolve(null),
          storage.get("fbz8-factlog"),
          // LOT Incidents 13 — UNIFICATION : les incidents admin proviennent
          // désormais de la table V2 `incidents` (source UNIQUE), plus du KV
          // legacy fbz8-incidents. Mappés en shape KV-compat (logId/propId/
          // createdAt) plus bas, une fois cfg disponible (résolution propId).
          // limit élargi : la facture proprio itère TOUT l'historique (filtrage
          // par mois de remplacement fait côté facture, pas par created_at).
          listIncidents({ limit: 1000 }).catch(e => { console.error('[incidents] fetch V2 failed', e); return [] }),
          isAdminUser
            ? comptaApi.fetchCompta().catch(replier('compta', null))
            : Promise.resolve(null),
          // L'agent de ménage n'a aucun accès au stock (décision 53) : le
          // serveur le refuserait d'entrée (403), on ne le demande pas.
          currentUser.role === 'menage'
            ? Promise.resolve(null)
            : stockApi.fetchStock().catch(replier('stock', null)),
        ])
        if (cancelled) return
        // F13 (relecture adversariale, tour 2) : `ready` passe vrai avant la
        // fin de ce chargement ; une relecture du rafraîchissement a pu RÉUSSIR
        // entre-temps (données et jeton adoptés). Un échec de CE chargement-ci
        // ne doit alors pas remettre « Lecture impossible » : les données sont
        // lues, et le bandeau restait collé (écran Argent en « — », factures
        // bloquées). Tour 3 : seulement si le jeton a changé PENDANT ce
        // chargement (`jetonDebut`) — un jeton d'avant ne prouve rien.
        illisibles.splice(0, illisibles.length, ...pannesDuChargement({ messages: illisibles, chargementRate: !dr, jetonDebut }))
        // ⚠️ AVANT tout le reste : c'est la seule chose qui empêche l'écran de
        // s'ouvrir vide et muet. Posé même quand `illisibles` est vide — une
        // lecture qui repasse doit effacer son avertissement.
        setLecturesIllisibles(Array.from(new Set(illisibles)))
        if (cfgRes) setCfg(cfgRes)
        // ⚠️ LE CATALOGUE VIENT DU SERVEUR, PLUS DU BUNDLE (Dimitri 2026-08-23).
        // L'agent et l'orchestrateur ne peuvent pas lire la configuration
        // (table `storage` = admin seulement) : leur catalogue était donc la
        // COPIE FIGÉE embarquée dans le code — périmée dès le premier produit
        // ajouté, et porteuse des PRIX D'ACHAT dans le navigateur de tous.
        // L'endpoint `catalogue_consommables` sert nom + format + unité, ZÉRO
        // montant. L'admin, lui, garde le catalogue complet de `cfg`.
        if (!isAdminUser) {
          try {
            const { data: { session } } = await supabase.auth.getSession()
            if (session?.access_token) {
              const rep = await fetch('/api/admin-users?action=catalogue_consommables', {
                headers: { Authorization: `Bearer ${session.access_token}` },
              })
              if (rep.ok) {
                const json = await rep.json().catch(() => ({}))
                if (Array.isArray(json?.consommables)) {
                  setCfg((prec) => ({ ...(prec || {}), consommables: json.consommables }))
                }
              }
            }
          } catch (e) {
            // Sans catalogue, les écrans nomment « produit retiré du catalogue » —
            // visible, jamais silencieux.
            console.error('[cfg] catalogue consommables', e?.message || e)
          }
        }
        // `dr` = `{ data, jeton }` rendu par /api/data (F13) : la donnée ET
        // son jeton s'adoptent ENSEMBLE, ici, une fois le chargement retombé
        // et non annulé — jamais l'un sans l'autre.
        if (dr) {
          adopterJeton(dr.jeton)
          if (dr.data && Object.keys(dr.data).length > 0) setData(migrateData(dr.data))
        }
        if (fr) setFactLog(JSON.parse(fr.value))
        // propId résolu via getAllLogsFlat (dédup byId, JAMAIS par nom) sur le
        // cfg fraîchement chargé (cfgRes) — fallback null si cfg indisponible.
        const propByLog = new Map(
          (cfgRes ? getAllLogsFlat(cfgRes) : []).map(l => [l.id, l.propId])
        )
        setIncidents(mapV2IncidentsToKv(incRows, propByLog))
        if (compta) setCompta(compta)
        else setCompta({ chargesFixes: CHARGES_FIXES_DEFAULT, depensesVar: {}, consommables: {} })
        if (stock) {
          setStockCentral(stock.stockCentral || {})
          setDepots(stock.depots || [])
          setAchats(stock.achats || [])
        }
      } catch(e) { console.error('Data load failed:', e) }
    })()
    return () => { cancelled = true }
  }, [currentUser?.id])
  return {  }
}
